import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Calendar, Phone, Mail, User, TrendingUp, AlertTriangle, Download } from 'lucide-react';
import { getProposal } from '../utils/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { RUBICON_LOGO_BASE64 } from '../utils/pdfAssets';


const ViewProposal = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProposal(id)
      .then(setProposal)
      .catch(() => {
        alert('Proposal not found');
        navigate('/admin/dashboard');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format currency for PDF (using Rs. for compatibility)
  const formatCurrencyForPDF = (amount) => {
    const formatted = new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0,
    }).format(amount);
    return `Rs. ${formatted}`;
  };

  // Sanitise free-text fields for jsPDF (default Helvetica is Latin-1 only).
  // Replaces unsupported characters (₹, curly quotes, em-dash, etc.) with safe equivalents
  // to avoid the broken character-spacing bug where each letter renders far apart.
  const sanitizeForPDF = (text) => {
    if (text == null) return '';
    return String(text)
      .replace(/₹/g, 'Rs. ')
      .replace(/[\u2018\u2019\u2032]/g, "'")     // curly single quotes / prime
      .replace(/[\u201C\u201D\u2033]/g, '"')     // curly double quotes / double prime
      .replace(/[\u2013\u2014]/g, '-')           // en-dash / em-dash
      .replace(/[\u2026]/g, '...')               // ellipsis
      .replace(/[\u00A0]/g, ' ')                 // non-breaking space
      .replace(/[\u2192\u27F6\u27A1\u21D2\u21E8]/g, '-->')  // right-arrow variants → --> (STP source-to-target)
      .replace(/[\u2190\u27F5\u21D0]/g, '<--')             // left-arrow variants
      .replace(/[\u2194\u27F7\u21D4]/g, '<-->')            // bidirectional
      .replace(/[^\x20-\x7E\n\r\t]/g, '');       // strip any remaining non-Latin1
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateCorpus = (rec) => {
    const months = rec.tenure_months;
    const annualReturn = rec.expected_return / 100;
    const monthlyReturn = annualReturn / 12;

    // Industry convention for Indian Daily SIP: ~22 working days/month → 264 days/year
    const WORKING_DAYS_PER_MONTH = 22;
    const WORKING_DAYS_PER_YEAR = 264;

    if (rec.investment_type === 'SIP') {
      if (rec.sip_frequency === 'Daily') {
        // Daily SIP — compound daily with 264 working days/year
        const dailyReturn = annualReturn / WORKING_DAYS_PER_YEAR;
        const totalDays = months * WORKING_DAYS_PER_MONTH;
        if (dailyReturn === 0) return rec.investment_amount * totalDays;
        const fv = rec.investment_amount * (((Math.pow(1 + dailyReturn, totalDays) - 1) / dailyReturn) * (1 + dailyReturn));
        return fv;
      }
      // Monthly SIP — compound monthly (Annuity Due)
      const fv = rec.investment_amount * (((Math.pow(1 + monthlyReturn, months) - 1) / monthlyReturn) * (1 + monthlyReturn));
      return fv;
    } else if (rec.investment_type === 'STP') {
      // STP simulates two buckets: source (debt) → target (equity).
      // Initial lumpsum sits in source, earns source_return, transfers out gradually to target.
      const freq = rec.stp_frequency || 'Monthly';
      const stpMonths = rec.tenure_months;
      const holdMonths = Math.max(rec.holding_period_months || stpMonths, stpMonths);
      const initialLumpsum = rec.initial_lumpsum || 0;
      const sourceReturn = (rec.source_return != null ? rec.source_return : 6) / 100;

      // Determine period rate & instalments per year for chosen frequency
      let periods, periodRateSource, periodRateTarget;
      if (freq === 'Daily') {
        periods = { total: stpMonths * WORKING_DAYS_PER_MONTH, perMonth: WORKING_DAYS_PER_MONTH };
        periodRateSource = sourceReturn / WORKING_DAYS_PER_YEAR;
        periodRateTarget = annualReturn / WORKING_DAYS_PER_YEAR;
      } else if (freq === 'Weekly') {
        periods = { total: stpMonths * 4, perMonth: 4 };
        periodRateSource = sourceReturn / 48;
        periodRateTarget = annualReturn / 48;
      } else {
        periods = { total: stpMonths, perMonth: 1 };
        periodRateSource = sourceReturn / 12;
        periodRateTarget = annualReturn / 12;
      }

      // Simulate the two buckets during STP period
      let source = initialLumpsum;
      let target = 0;
      const transfer = rec.investment_amount;
      for (let i = 0; i < periods.total; i++) {
        source = source * (1 + periodRateSource);
        // Transfer whatever source has, capped at requested transfer amount
        const actualTransfer = Math.min(transfer, source);
        source -= actualTransfer;
        target = (target + actualTransfer) * (1 + periodRateTarget);
      }

      // Post-STP hold — both buckets keep growing at their respective monthly rates
      const postStpMonths = Math.max(0, holdMonths - stpMonths);
      if (postStpMonths > 0) {
        source = source * Math.pow(1 + sourceReturn / 12, postStpMonths);
        target = target * Math.pow(1 + monthlyReturn, postStpMonths);
      }

      return source + target;
    } else if (rec.investment_type === 'SWP') {
      // SWP: investment_amount is monthly withdrawal. Show total withdrawn.
      const totalWithdrawn = rec.investment_amount * months;
      return totalWithdrawn;
    } else {
      // Lumpsum Future Value formula
      const fv = rec.investment_amount * Math.pow(1 + monthlyReturn, months);
      return fv;
    }
  };

  const downloadPDF = () => {
    if (!proposal) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 15;

    // Add page footer function
    const addFooter = (pageNum) => {
      const footerY = pageHeight - 25;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      
      // Address and contact
      doc.text('6A Dr.Rajendra Prasad Sarani, 4th Floor, R.No-20, Kolkata-700001', pageWidth / 2, footerY, { align: 'center' });
      doc.text('Email: communications@rubiconfinservices.com | Website: www.rubiconfinservices.com | Tel: +91-33-40442577', pageWidth / 2, footerY + 4, { align: 'center' });
      
      // Quote
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text("\"If you think about the long term, you can take good life decisions which you won't regret later\"", pageWidth / 2, footerY + 9, { align: 'center' });
      
      // Page number
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Page ${pageNum}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
      
      doc.setTextColor(0, 0, 0);
    };

    // Add embedded logo directly
    try {
      // Logo dimensions: 300x288 (aspect ratio 1.042)
      const logoWidth = 50;  // Increased from 40mm
      const logoHeight = 48; // Increased from 38.4mm (maintains ratio)
      doc.addImage(RUBICON_LOGO_BASE64, 'JPEG', (pageWidth - logoWidth) / 2, yPos, logoWidth, logoHeight);
      yPos += 52;
    } catch (error) {
      console.error('Error adding logo to PDF:', error);
      // Fallback to text header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      doc.setTextColor(0, 51, 102);
      doc.text('RUBICON FINSERVICES', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
    }

    // Title
    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    doc.text('Investment Proposal', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('AMFI Registered Mutual Fund Distributor', pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
    doc.setFontSize(8);
    doc.text('ARN: 102867 | Initial Registration: 09-June-2015 | Valid till: 01-07-2027', pageWidth / 2, yPos, { align: 'center' });
    yPos += 3.5;
    doc.text('SIF: Valid till 15-03-2029  |  APRN: Valid till 17-10-2027  |  APMI: APMI-APRN01145', pageWidth / 2, yPos, { align: 'center' });

    yPos += 3;
    doc.setLineWidth(0.5);
    doc.setDrawColor(0, 51, 102);
    doc.line(14, yPos, pageWidth - 14, yPos);
    yPos += 10;

    // Proposal Details
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const proposalDate = new Date(proposal.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
    const validDate = new Date(proposal.valid_until).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(`Date: ${proposalDate}`, 14, yPos);
    doc.text(`Valid Until: ${validDate}`, pageWidth - 14, yPos, { align: 'right' });
    yPos += 8;

    // Client Information Section
    doc.setFillColor(240, 240, 255);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 51, 102);
    doc.text('CLIENT INFORMATION', 16, yPos + 5);
    yPos += 12;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    const clientInfo = [
      ['Client Name:', sanitizeForPDF(proposal.client_name)],
      ['Email:', sanitizeForPDF(proposal.client_email)],
      ['Phone:', sanitizeForPDF(proposal.client_phone)],
      ['Risk Profile:', sanitizeForPDF(proposal.risk_profile)],
      ['Investment Horizon:', sanitizeForPDF(proposal.investment_horizon || 'Not specified')],
      ['Lumpsum Investment:', proposal.lumpsum_investment ? formatCurrencyForPDF(proposal.lumpsum_investment) : 'Not specified'],
      ['Monthly SIP:', proposal.sip_per_month ? formatCurrencyForPDF(proposal.sip_per_month) : 'Not specified'],
      ['Daily SIP:', proposal.daily_sip ? formatCurrencyForPDF(proposal.daily_sip) : 'Not specified'],
      ['STP Per Month:', proposal.stp_per_month ? formatCurrencyForPDF(proposal.stp_per_month) : 'Not specified'],
      ['SWP Per Month:', proposal.swp_per_month ? formatCurrencyForPDF(proposal.swp_per_month) : 'Not specified']
    ];

    clientInfo.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 16, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 60, yPos);
      yPos += 6;
    });

    // Investment Objective - wrapped, after the other one-liner fields
    yPos += 2;
    doc.setFont('helvetica', 'bold');
    doc.text('Investment Objective:', 16, yPos);
    doc.setFont('helvetica', 'normal');
    const objText = sanitizeForPDF(proposal.investment_objective || 'Not specified');
    const objLines = doc.splitTextToSize(objText, pageWidth - 60 - 14);
    doc.text(objLines, 60, yPos);
    yPos += objLines.length * 5 + 4;

    // Financial Goals - wrapped paragraph
    doc.setFont('helvetica', 'bold');
    doc.text('Financial Goals:', 16, yPos);
    doc.setFont('helvetica', 'normal');
    const goalText = sanitizeForPDF(proposal.financial_goals || 'Not specified');
    const goalLines = doc.splitTextToSize(goalText, pageWidth - 60 - 14);
    doc.text(goalLines, 60, yPos);
    yPos += goalLines.length * 5 + 8;

    // Investment Recommendations Section — always on a new page for clarity
    addFooter(1);
    doc.addPage();
    let pageNum = 2;
    yPos = 20;

    doc.setFillColor(240, 240, 255);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 51, 102);
    doc.text('INVESTMENT RECOMMENDATIONS', 16, yPos + 5);
    yPos += 12;

    proposal.recommendations.forEach((rec, index) => {
      const corpus = calculateCorpus(rec);
      const months = rec.tenure_months;
      // totalInvested calculation by investment type
      let totalInvested;
      if (rec.investment_type === 'SIP') {
        const perMonth = rec.investment_amount * (rec.sip_frequency === 'Daily' ? 22 : 1);
        totalInvested = perMonth * months;
      } else if (rec.investment_type === 'STP') {
        // For STP: total capital deployed = initial lumpsum. Transfers are internal reshuffling.
        // If no lumpsum specified, fall back to sum of transfers.
        if (rec.initial_lumpsum && rec.initial_lumpsum > 0) {
          totalInvested = rec.initial_lumpsum;
        } else {
          const freq = rec.stp_frequency || 'Monthly';
          const perMonth = freq === 'Daily' ? 22 : freq === 'Weekly' ? 4 : 1;
          totalInvested = rec.investment_amount * perMonth * months;
        }
      } else if (rec.investment_type === 'SWP') {
        totalInvested = rec.investment_amount * months; // total withdrawn
      } else {
        totalInvested = rec.investment_amount;
      }
      const gains = corpus - totalInvested;

      // Check if we need a new page
      if (yPos > pageHeight - 80) {
        addFooter(pageNum);
        doc.addPage();
        pageNum++;
        yPos = 20;
      }

      // Scheme heading
      doc.setFillColor(250, 250, 250);
      doc.rect(14, yPos - 4, pageWidth - 28, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(`${index + 1}. ${sanitizeForPDF(rec.scheme_name)}`, 16, yPos);
      yPos += 8;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(`AMC: ${sanitizeForPDF(rec.amc)}`, 16, yPos);
      doc.text(`Category: ${sanitizeForPDF(rec.category)}`, 120, yPos);
      yPos += 6;

      // Exit Load line(s) — source (STP/SWP only) + target
      const isDualForEL = rec.investment_type === 'STP' || rec.investment_type === 'SWP';
      const targetHasEL = rec.exit_load_percent != null || rec.exit_load_days != null;
      const sourceHasEL = isDualForEL && (rec.source_exit_load_percent != null || rec.source_exit_load_days != null);
      if (sourceHasEL || targetHasEL) {
        const buildELText = (pct, days) => {
          const p = pct != null ? Number(pct) : 0;
          const d = days != null ? Number(days) : 365;
          return p > 0
            ? `${p.toFixed(2)}% if redeemed within ${d} days`
            : 'Nil';
        };
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(139, 69, 19);
        if (sourceHasEL) {
          doc.text(
            sanitizeForPDF(`${isDualForEL ? 'Source Exit Load' : 'Exit Load'}: ${buildELText(rec.source_exit_load_percent, rec.source_exit_load_days)}`),
            16,
            yPos
          );
          yPos += 5;
        }
        if (targetHasEL) {
          doc.text(
            sanitizeForPDF(`${isDualForEL ? 'Target Exit Load' : 'Exit Load'}: ${buildELText(rec.exit_load_percent, rec.exit_load_days)}`),
            16,
            yPos
          );
          yPos += 5;
        }
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        yPos += 1;
      }

      // Expense Ratio & Trail Commission disclosure
      const isStpSwp = rec.investment_type === 'STP' || rec.investment_type === 'SWP';
      const erBits = [];
      if (rec.source_expense_ratio != null && isStpSwp) erBits.push(`Source Expense Ratio: ${Number(rec.source_expense_ratio).toFixed(2)}%`);
      if (rec.expense_ratio != null) erBits.push(`${isStpSwp ? 'Target Expense Ratio' : 'Expense Ratio'}: ${Number(rec.expense_ratio).toFixed(2)}%`);
      if (erBits.length) {
        doc.text(sanitizeForPDF(erBits.join('    |    ')), 16, yPos);
        yPos += 6;
      }
      if (rec.trail_commission != null) {
        doc.text(sanitizeForPDF(`Trail Commission: ${Number(rec.trail_commission).toFixed(2)}% p.a.`), 16, yPos);
        yPos += 6;
      }

      // For STP: show source -> target on separate lines
      if (rec.investment_type === 'STP') {
        doc.text(`Switch Out: ${sanitizeForPDF(rec.switch_out_scheme || '-')}`, 16, yPos);
        yPos += 5;
        doc.text(`Switch In:  ${sanitizeForPDF(rec.switch_in_scheme || '-')}`, 16, yPos);
        yPos += 5;
        if (rec.initial_lumpsum && rec.initial_lumpsum > 0) {
          doc.text(`Initial Lumpsum in Source: ${formatCurrencyForPDF(rec.initial_lumpsum)} @ ${rec.source_return || 6}% p.a.`, 16, yPos);
          yPos += 5;
        }
        yPos += 1;
      }

      // Build investment details per type
      const typeLabel = rec.investment_type === 'SIP'
        ? `SIP (${rec.sip_frequency || 'Monthly'})`
        : rec.investment_type === 'STP'
          ? `STP (${rec.stp_frequency || 'Monthly'})`
          : rec.investment_type;
      const amountLabel = rec.investment_type === 'SIP'
        ? (rec.sip_frequency === 'Daily' ? 'Daily SIP Amount:' : 'Monthly SIP Amount:')
        : rec.investment_type === 'STP'
          ? ((rec.stp_frequency === 'Daily' ? 'Daily' : rec.stp_frequency === 'Weekly' ? 'Weekly' : 'Monthly') + ' Transfer Amount:')
          : rec.investment_type === 'SWP'
            ? 'Monthly Withdrawal:'
            : 'Lumpsum Amount:';
      const totalLabel = rec.investment_type === 'SWP' ? 'Total Withdrawn:' : 'Total Investment:';
      const details = [
        ['Investment Type:', typeLabel],
        [amountLabel, formatCurrencyForPDF(rec.investment_amount)],
        ['Tenure:', rec.investment_type === 'STP'
          ? `STP ${rec.tenure_months} months, Total Holding ${rec.holding_period_months || rec.tenure_months} months`
          : `${rec.tenure_months} Months`],
        ['Assumed Rate of Return*:', `${rec.expected_return}% p.a.`],
        [totalLabel, formatCurrencyForPDF(totalInvested)]
      ];

      doc.setFontSize(9);
      details.forEach(([label, value]) => {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(label, 18, yPos);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(value, 80, yPos);
        yPos += 5;
      });

      yPos += 2;
      
      // Corpus and Gains (highlighted)
      doc.setFillColor(255, 250, 230);
      doc.rect(16, yPos - 3, pageWidth - 32, 12, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(139, 69, 19);
      doc.text('Estimated Corpus:', 18, yPos);
      doc.setTextColor(0, 100, 0);
      doc.text(formatCurrencyForPDF(corpus), 80, yPos);
      
      doc.setTextColor(139, 69, 19);
      doc.text('Estimated Gains:', 18, yPos + 5);
      doc.setTextColor(0, 128, 0);
      doc.text(formatCurrencyForPDF(gains), 80, yPos + 5);
      
      yPos += 15;

      // Scheme Returns & Benchmark Comparison (only if any value present)
      const hasReturns =
        rec.returns_1y != null || rec.returns_3y != null || rec.returns_5y != null || rec.returns_since_inception != null ||
        rec.bench_1y != null || rec.bench_3y != null || rec.bench_5y != null || rec.bench_since_inception != null ||
        rec.source_returns_1y != null || rec.source_returns_3y != null || rec.source_returns_5y != null || rec.source_returns_since_inception != null ||
        rec.source_bench_1y != null || rec.source_bench_3y != null || rec.source_bench_5y != null || rec.source_bench_since_inception != null;
      if (hasReturns) {
        const isDual = rec.investment_type === 'STP' || rec.investment_type === 'SWP';
        // Row heights: header=7 + scheme rows=13 (2 sub-labels) + benchmark rows=10 (1 sub-label)
        const heightNeeded = isDual ? (15 + 7 + 13 + 10 + 13 + 10 + 6) : (15 + 7 + 13 + 10 + 6);

        // Page break if not enough space
        if (yPos > pageHeight - (heightNeeded + 15)) {
          addFooter(pageNum);
          doc.addPage();
          pageNum++;
          yPos = 20;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(30, 58, 138);
        doc.text(
          isDual
            ? 'Scheme Returns vs Benchmark — Source & Target (% CAGR)'
            : 'Scheme Returns vs Benchmark (% CAGR)',
          18,
          yPos
        );
        // Header inception is now redundant — each fund row shows its own inception below.
        yPos += 4;

        // Table geometry
        const tblX = 18;
        const tblW = pageWidth - 36;
        const colLabelW = 55;
        const colDataW = (tblW - colLabelW) / 4;
        const rowH = 7;

        // Header row
        doc.setFillColor(240, 249, 255);
        doc.rect(tblX, yPos, tblW, rowH, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.rect(tblX, yPos, tblW, rowH);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(30, 58, 138);
        doc.text('Period', tblX + 2, yPos + 5);
        ['1 Year', '3 Year', '5 Year', 'Since Inception'].forEach((h, i) => {
          doc.text(h, tblX + colLabelW + colDataW * i + colDataW / 2, yPos + 5, { align: 'center' });
        });
        yPos += rowH;

        // Format helper
        const pct = (v) => (v == null || v === '' ? '—' : `${Number(v).toFixed(2)}%`);

        // Date formatter for inception dates in per-fund rows
        const fmtIncDate = (iso) => {
          if (!iso) return '';
          try {
            return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
          } catch (_) { return ''; }
        };

        // Truncate helper for the main label (shouldn't trigger for short main labels)
        const maxLabelChars = 34;
        const trunc = (s) => (s.length > maxLabelChars ? s.slice(0, maxLabelChars - 1) + '…' : s);

        // Render a single data row.
        // subLabel1 = scheme/benchmark name (small grey line 1)
        // subLabel2 = inception date line (small grey line 2, italic)
        // Row height auto-expands: 7mm base + 3mm per sub-label present.
        const renderRow = (labelText, values, bgFill, subLabel1 = '', subLabel2 = '') => {
          const extraLines = (subLabel1 ? 1 : 0) + (subLabel2 ? 1 : 0);
          const rowHeight = rowH + extraLines * 3;
          if (bgFill) {
            doc.setFillColor(...bgFill);
            doc.rect(tblX, yPos, tblW, rowHeight, 'F');
          }
          doc.setDrawColor(220, 220, 220);
          doc.rect(tblX, yPos, tblW, rowHeight);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(8.5);
          doc.text(trunc(labelText), tblX + 2, yPos + 5);

          let subY = yPos + 9;
          if (subLabel1) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(110, 110, 110);
            const s1 = subLabel1.length > 60 ? subLabel1.slice(0, 59) + '…' : subLabel1;
            doc.text(s1, tblX + 2, subY);
            subY += 3;
          }
          if (subLabel2) {
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(7);
            doc.setTextColor(130, 130, 130);
            doc.text(subLabel2, tblX + 2, subY);
          }
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'normal');

          doc.setFontSize(8.5);
          // Vertically centre the data values in the row
          const dataY = yPos + (rowHeight / 2) + 1.5;
          values.forEach((v, i) => {
            doc.text(pct(v), tblX + colLabelW + colDataW * i + colDataW / 2, dataY, { align: 'center' });
          });
          yPos += rowHeight;
        };

        // If dual: source scheme + source benchmark first, then target scheme + target benchmark
        if (isDual) {
          renderRow(
            'Source Scheme',
            [rec.source_returns_1y, rec.source_returns_3y, rec.source_returns_5y, rec.source_returns_since_inception],
            [254, 243, 199],
            rec.source_scheme_name || '',
            rec.source_fund_inception_date ? `Inception: ${fmtIncDate(rec.source_fund_inception_date)}` : ''
          );
          renderRow(
            'Source Benchmark',
            [rec.source_bench_1y, rec.source_bench_3y, rec.source_bench_5y, rec.source_bench_since_inception],
            [254, 243, 199],
            rec.source_benchmark_name || ''
          );
        }

        renderRow(
          isDual ? 'Target Scheme' : 'Scheme',
          [rec.returns_1y, rec.returns_3y, rec.returns_5y, rec.returns_since_inception],
          null,
          rec.target_scheme_name || rec.scheme_name || '',
          rec.fund_inception_date ? `Inception: ${fmtIncDate(rec.fund_inception_date)}` : ''
        );
        renderRow(
          isDual ? 'Target Benchmark' : 'Benchmark',
          [rec.bench_1y, rec.bench_3y, rec.bench_5y, rec.bench_since_inception],
          [250, 250, 250],
          rec.benchmark_name || ''
        );

        yPos += 6;
      }
    });

    // TOTAL SUMMARY BOX - Calculate totals from all recommendations
    const totalInvestmentAll = proposal.recommendations.reduce((sum, rec) => {
      if (rec.investment_type === 'SIP') {
        const perMonth = rec.investment_amount * (rec.sip_frequency === 'Daily' ? 22 : 1);
        return sum + perMonth * rec.tenure_months;
      }
      if (rec.investment_type === 'STP') {
        if (rec.initial_lumpsum && rec.initial_lumpsum > 0) {
          return sum + rec.initial_lumpsum;
        }
        const freq = rec.stp_frequency || 'Monthly';
        const perMonth = freq === 'Daily' ? 22 : freq === 'Weekly' ? 4 : 1;
        return sum + rec.investment_amount * perMonth * rec.tenure_months;
      }
      if (rec.investment_type === 'SWP') {
        return sum + rec.investment_amount * rec.tenure_months;
      }
      return sum + rec.investment_amount;
    }, 0);
    
    const totalCorpusAll = proposal.recommendations.reduce((sum, rec) => {
      return sum + calculateCorpus(rec);
    }, 0);
    
    const totalGainsAll = totalCorpusAll - totalInvestmentAll;

    // Check if we need a new page
    if (yPos > pageHeight - 80) {
      addFooter(pageNum);
      doc.addPage();
      pageNum++;
      yPos = 20;
    }

    // Total Summary Header
    doc.setFillColor(0, 51, 102);
    doc.rect(14, yPos - 4, pageWidth - 28, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text('TOTAL INVESTMENT SUMMARY', 16, yPos + 2);
    yPos += 12;

    // Total Investment Summary Box
    doc.setFillColor(245, 245, 250);
    doc.rect(14, yPos - 3, pageWidth - 28, 20, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 51, 102);
    doc.text('Total Investment:', 18, yPos);
    doc.setTextColor(0, 0, 0);
    doc.text(formatCurrencyForPDF(totalInvestmentAll), 80, yPos);
    
    doc.setTextColor(0, 51, 102);
    doc.text('Total Expected Corpus:', 18, yPos + 6);
    doc.setTextColor(0, 100, 0);
    doc.setFontSize(12);
    doc.text(formatCurrencyForPDF(totalCorpusAll), 80, yPos + 6);
    
    doc.setFontSize(11);
    doc.setTextColor(0, 51, 102);
    doc.text('Total Expected Gains:', 18, yPos + 12);
    doc.setTextColor(0, 128, 0);
    doc.text(formatCurrencyForPDF(totalGainsAll), 80, yPos + 12);
    
    yPos += 25;

    // Add disclaimer note for assumed rate of return (ALWAYS show)
    if (yPos > pageHeight - 40) {
      addFooter(pageNum);
      doc.addPage();
      pageNum++;
      yPos = 20;
    }

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    const rateNote = '*Assumed Rate of Return: The returns shown are assumed/indicative and not guaranteed. Actual returns may vary based on market conditions. Past performance is not indicative of future results.';
    const splitNote = doc.splitTextToSize(rateNote, pageWidth - 36);
    // Dynamic box height (8pt italic ≈ 3.2mm/line) + top/bottom padding
    const rateBoxHeight = Math.max(10, splitNote.length * 3.5 + 4);
    doc.setFillColor(255, 240, 240);
    doc.rect(16, yPos, pageWidth - 32, rateBoxHeight, 'F');
    doc.setTextColor(180, 0, 0);
    doc.text(splitNote, 18, yPos + 5);
    yPos += rateBoxHeight + 5;

    // Additional Notes
    if (proposal.notes) {
      yPos += 5;

    if (yPos > pageHeight - 60) {
        addFooter(pageNum);
        doc.addPage();
        pageNum++;
        yPos = 20;
      }

      doc.setFillColor(240, 240, 255);
      doc.rect(14, yPos, pageWidth - 28, 7, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0, 51, 102);
      doc.text('ADDITIONAL NOTES', 16, yPos + 5);
      yPos += 12;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      const notesLines = doc.splitTextToSize(sanitizeForPDF(proposal.notes), pageWidth - 32);
      doc.text(notesLines, 16, yPos);
      yPos += notesLines.length * 5 + 8;
    }

    // Disclaimer
    if (yPos > pageHeight - 70) {
      addFooter(pageNum);
      doc.addPage();
      pageNum++;
      yPos = 20;
    }

    // Build the disclaimer text and wrap it to know how tall the box needs to be
    const disclaimerText = `STATUTORY DISCLAIMER: Mutual Fund investments are subject to market risks, read all scheme related documents carefully before investing. The NAVs of the schemes may go up or down depending upon the factors and forces affecting the securities market including the fluctuations in the interest rates. Past performance is not indicative of future returns. The sponsor/AMC is not responsible or liable for any loss or shortfall resulting from the operation of the schemes. Investors should deal only with registered Mutual Funds, details of which can be verified on SEBI/AMFI website. COMMISSION DISCLOSURE: Rubicon Finservices earns trail commission only from AMCs. No fees charged from investors. AMFI Registration ARN: 102867 (Valid till 01-07-2027) | SIF (Valid till 15-03-2029) | APRN (Valid till 17-10-2027). MSME: UDYAM-WB-14-0192124. This proposal is for information purposes only and should not be construed as an offer. Investors should consult their own tax consultant and financial advisor before making any investment decision. For grievances: Prem Prakash Bhansali (Grievance Officer) - complaints@rubiconfinservices.com`;

    doc.setFontSize(7);
    const splitDisclaimer = doc.splitTextToSize(disclaimerText, pageWidth - 32);
    // Dynamic height: 6 (title) + lines*3 (7pt text ≈ 3mm per line) + 6 padding
    const disclaimerBoxHeight = 6 + splitDisclaimer.length * 3 + 6;

    // If the box would overflow into the footer area, push it onto a new page
    const footerStartY = pageHeight - 25;
    if (yPos - 2 + disclaimerBoxHeight > footerStartY - 4) {
      addFooter(pageNum);
      doc.addPage();
      pageNum++;
      yPos = 20;
    }

    doc.setFillColor(255, 240, 240);
    doc.rect(14, yPos - 2, pageWidth - 28, disclaimerBoxHeight, 'F');
    doc.setDrawColor(200, 0, 0);
    doc.setLineWidth(1);
    doc.rect(14, yPos - 2, pageWidth - 28, disclaimerBoxHeight);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(150, 0, 0);
    doc.text('IMPORTANT DISCLAIMER', 16, yPos + 4);
    yPos += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(80, 0, 0);
    doc.text(splitDisclaimer, 16, yPos);

    // Add footer to last page
    addFooter(pageNum);

    // Save PDF
    const fileName = `Proposal_${proposal.client_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading proposal...</p>
        </div>
      </div>
    );
  }

  if (!proposal) return null;

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin/dashboard">
            <Button variant="outline" size="sm">
              <ArrowLeft size={16} className="mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">Proposal Details</h1>
        </div>

        {/* Proposal Content */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          {/* Header with Logo */}
          <div className="text-center mb-8 border-b pb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_rubicon-funds/artifacts/7akuxub4_LOGO.png" 
              alt="Rubicon Finservices" 
              className="h-32 mx-auto mb-4"
            />
            <h2 className="text-2xl font-bold text-slate-900">Investment Proposal</h2>
            <p className="text-slate-600 mt-2">AMFI Registered Mutual Fund Distributor</p>
            <p className="text-xs text-slate-500 mt-1">ARN: 102867 | Initial Registration: 09-June-2015 | Valid till: 01-07-2027</p>
            <p className="text-xs text-slate-500">SIF: Valid till 15-03-2029 | APRN: Valid till 17-10-2027 | APMI: APMI-APRN01145</p>
          </div>

          {/* Client Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User size={20} />
                Client Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Basic Info in Grid */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-sm text-slate-600">Client Name</p>
                  <p className="font-semibold text-slate-900">{proposal.client_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Email</p>
                  <p className="font-semibold text-slate-900 flex items-center gap-2">
                    <Mail size={16} />
                    {proposal.client_email}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-sm text-slate-600">Phone</p>
                  <p className="font-semibold text-slate-900 flex items-center gap-2">
                    <Phone size={16} />
                    {proposal.client_phone}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Risk Profile</p>
                  <p className="font-semibold text-slate-900">{proposal.risk_profile}</p>
                </div>
              </div>

              {/* Investment Horizon */}
              <div className="mb-6">
                <p className="text-sm text-slate-600 mb-1">Investment Horizon</p>
                <p className="font-semibold text-slate-900">{proposal.investment_horizon || 'Not specified'}</p>
              </div>

              {/* Financial Goals - MOVED UP */}
              <div className="mb-6">
                <p className="text-sm text-slate-600 mb-2 font-bold">Financial Goals</p>
                <p className="text-slate-900 whitespace-pre-wrap leading-relaxed">
                  {proposal.financial_goals}
                </p>
              </div>

              {/* Investment Details */}
              <div className="border-t-4 border-slate-400 pt-6 mb-6">
                <h4 className="text-lg font-bold text-slate-900 mb-4">INVESTMENT DETAILS</h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Lumpsum Investment</p>
                    <p className="font-bold text-green-600 text-xl">
                      {proposal.lumpsum_investment ? formatCurrency(proposal.lumpsum_investment) : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Monthly SIP</p>
                    <p className="font-bold text-green-600 text-xl">
                      {proposal.sip_per_month ? formatCurrency(proposal.sip_per_month) : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Daily SIP</p>
                    <p className="font-bold text-green-600 text-xl">
                      {proposal.daily_sip ? formatCurrency(proposal.daily_sip) : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">STP Per Month</p>
                    <p className="font-bold text-green-600 text-xl">
                      {proposal.stp_per_month ? formatCurrency(proposal.stp_per_month) : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">SWP Per Month</p>
                    <p className="font-bold text-green-600 text-xl">
                      {proposal.swp_per_month ? formatCurrency(proposal.swp_per_month) : 'Not specified'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="mb-6">
                <p className="text-sm text-slate-600 mb-2">Status</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  proposal.status === 'Draft' ? 'bg-gray-100 text-gray-700' :
                  proposal.status === 'Sent' ? 'bg-blue-100 text-blue-700' :
                  proposal.status === 'Accepted' ? 'bg-green-100 text-green-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {proposal.status}
                </span>
              </div>

              {/* Investment Objective - MOVED TO END IN SEPARATE BOX */}
              <div className="mt-8 pt-8 border-t-4 border-blue-500">
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6">
                  <h4 className="text-lg font-bold text-blue-900 mb-4 uppercase">Investment Objective</h4>
                  <p className="text-slate-900 whitespace-pre-wrap leading-relaxed text-base">
                    {proposal.investment_objective || 'Not specified'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Investment Recommendations */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp size={20} />
                Investment Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {proposal.recommendations.map((rec, index) => {
                const corpus = calculateCorpus(rec);
                const months = rec.tenure_months;
                let totalInvested;
                if (rec.investment_type === 'SIP') {
                  totalInvested = rec.investment_amount * (rec.sip_frequency === 'Daily' ? 22 : 1) * months;
                } else if (rec.investment_type === 'STP') {
                  if (rec.initial_lumpsum && rec.initial_lumpsum > 0) {
                    totalInvested = rec.initial_lumpsum;
                  } else {
                    const freq = rec.stp_frequency || 'Monthly';
                    const perMonth = freq === 'Daily' ? 22 : freq === 'Weekly' ? 4 : 1;
                    totalInvested = rec.investment_amount * perMonth * months;
                  }
                } else if (rec.investment_type === 'SWP') {
                  totalInvested = rec.investment_amount * months;
                } else {
                  totalInvested = rec.investment_amount;
                }
                const gains = corpus - totalInvested;

                const amountLabel = rec.investment_type === 'SIP'
                  ? (rec.sip_frequency === 'Daily' ? 'Daily Amount' : 'Monthly Amount')
                  : rec.investment_type === 'STP'
                    ? ((rec.stp_frequency === 'Daily' ? 'Daily' : rec.stp_frequency === 'Weekly' ? 'Weekly' : 'Monthly') + ' Transfer')
                    : rec.investment_type === 'SWP'
                      ? 'Monthly Withdrawal'
                      : 'Lumpsum Amount';
                const typeLabel = rec.investment_type === 'SIP'
                  ? `SIP (${rec.sip_frequency || 'Monthly'})`
                  : rec.investment_type === 'STP'
                    ? `STP (${rec.stp_frequency || 'Monthly'})`
                    : rec.investment_type;

                return (
                  <div key={index} className="border-2 border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-bold text-slate-900 text-lg">{rec.scheme_name}</h4>
                        <p className="text-slate-600 text-sm">{rec.amc}</p>
                        {rec.investment_type === 'STP' && (
                          <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                            <p><span className="font-semibold text-slate-700">Switch Out:</span> {rec.switch_out_scheme || '—'}</p>
                            <p><span className="font-semibold text-slate-700">Switch In:</span> {rec.switch_in_scheme || '—'}</p>
                            {rec.initial_lumpsum > 0 && (
                              <p><span className="font-semibold text-slate-700">Initial Lumpsum in Source:</span> {formatCurrency(rec.initial_lumpsum)} @ {rec.source_return || 6}% p.a.</p>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        {rec.category}
                      </span>
                    </div>

                    {/* Exit Load line — only render if any value is present */}
                    {(() => {
                      const isDual = rec.investment_type === 'STP' || rec.investment_type === 'SWP';
                      const targetHas = rec.exit_load_percent != null || rec.exit_load_days != null;
                      const sourceHas = isDual && (rec.source_exit_load_percent != null || rec.source_exit_load_days != null);
                      if (!targetHas && !sourceHas) return null;
                      const fmt = (pct, days) => {
                        if (pct != null && Number(pct) > 0) {
                          return `${Number(pct).toFixed(2)}% if redeemed within ${days || 365} days`;
                        }
                        return 'Nil';
                      };
                      return (
                        <div className="mb-3 text-sm text-slate-700 space-y-1" data-testid={`exit-load-line-${index}`}>
                          {sourceHas && (
                            <div>
                              <span className="font-semibold">{isDual ? 'Source Exit Load:' : 'Exit Load:'}</span>{' '}
                              {fmt(rec.source_exit_load_percent, rec.source_exit_load_days)}
                            </div>
                          )}
                          {targetHas && (
                            <div>
                              <span className="font-semibold">{isDual ? 'Target Exit Load:' : 'Exit Load:'}</span>{' '}
                              {fmt(rec.exit_load_percent, rec.exit_load_days)}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {(rec.expense_ratio != null || rec.trail_commission != null || rec.source_expense_ratio != null) && (
                      <div className="mb-3 text-sm text-slate-700 flex flex-wrap gap-x-6 gap-y-1" data-testid={`scheme-costs-${index}`}>
                        {rec.source_expense_ratio != null && (rec.investment_type === 'STP' || rec.investment_type === 'SWP') && (
                          <div><span className="font-semibold">Source Scheme Expense Ratio:</span> {Number(rec.source_expense_ratio).toFixed(2)}%</div>
                        )}
                        {rec.expense_ratio != null && (
                          <div><span className="font-semibold">{(rec.investment_type === 'STP' || rec.investment_type === 'SWP') ? 'Target Scheme Expense Ratio' : 'Expense Ratio'}:</span> {Number(rec.expense_ratio).toFixed(2)}%</div>
                        )}
                        {rec.trail_commission != null && (
                          <div><span className="font-semibold">Trail Commission:</span> {Number(rec.trail_commission).toFixed(2)}% p.a.</div>
                        )}
                      </div>
                    )}

                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-slate-50 rounded p-3">
                        <p className="text-xs text-slate-600">Investment Type</p>
                        <p className="font-bold text-slate-900">{typeLabel}</p>
                      </div>
                      <div className="bg-slate-50 rounded p-3">
                        <p className="text-xs text-slate-600">{amountLabel}</p>
                        <p className="font-bold text-green-600">{formatCurrency(rec.investment_amount)}</p>
                      </div>
                      <div className="bg-slate-50 rounded p-3">
                        <p className="text-xs text-slate-600">
                          {rec.investment_type === 'STP' ? 'STP / Holding Period' : 'Tenure'}
                        </p>
                        <p className="font-bold text-slate-900">
                          {rec.investment_type === 'STP'
                            ? `${rec.tenure_months} / ${rec.holding_period_months || rec.tenure_months} Months`
                            : `${rec.tenure_months} Months`}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-3">
                        <p className="text-xs text-slate-600">Assumed Rate of Return*</p>
                        <p className="font-bold text-blue-600">{rec.expected_return}% p.a.</p>
                      </div>
                      <div className="bg-amber-50 rounded p-3">
                        <p className="text-xs text-amber-700">
                          {rec.investment_type === 'SWP' ? 'Total Withdrawn' : 'Estimated Corpus'}
                        </p>
                        <p className="font-bold text-amber-900">{formatCurrency(corpus)}</p>
                      </div>
                      <div className="bg-green-50 rounded p-3">
                        <p className="text-xs text-green-700">
                          {rec.investment_type === 'SWP' ? 'Total Income vs Invested' : 'Estimated Gains'}
                        </p>
                        <p className="font-bold text-green-900">{formatCurrency(gains)}</p>
                      </div>
                    </div>

                    {/* Scheme Returns & Benchmark comparison — only show if any return value exists */}
                    {(rec.returns_1y != null || rec.returns_3y != null || rec.returns_5y != null || rec.returns_since_inception != null ||
                      rec.bench_1y != null || rec.bench_3y != null || rec.bench_5y != null || rec.bench_since_inception != null ||
                      rec.source_returns_1y != null || rec.source_returns_3y != null || rec.source_returns_5y != null || rec.source_returns_since_inception != null ||
                      rec.source_bench_1y != null || rec.source_bench_3y != null || rec.source_bench_5y != null || rec.source_bench_since_inception != null) && (() => {
                        const pct = (v) => (v == null || v === '' ? '—' : `${Number(v).toFixed(2)}%`);
                        const isDual = rec.investment_type === 'STP' || rec.investment_type === 'SWP';
                        const targetSchemeLabel = isDual ? 'Target Scheme' : 'Scheme';
                        return (
                      <div className="mt-4 border border-blue-100 rounded-lg overflow-hidden" data-testid={`returns-table-${index}`}>
                        <div className="bg-blue-50 px-4 py-2 flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-blue-900">
                            {isDual ? 'Scheme Returns vs Benchmark — Source & Target (% CAGR)' : 'Scheme Returns vs Benchmark (% CAGR)'}
                          </p>
                        </div>
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left px-3 py-2 font-semibold text-slate-700">&nbsp;</th>
                              <th className="text-right px-3 py-2 font-semibold text-slate-700">1 Year</th>
                              <th className="text-right px-3 py-2 font-semibold text-slate-700">3 Year</th>
                              <th className="text-right px-3 py-2 font-semibold text-slate-700">5 Year</th>
                              <th className="text-right px-3 py-2 font-semibold text-slate-700">Since Inception</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isDual && (
                              <>
                                <tr className="border-t border-slate-200 bg-amber-50">
                                  <td className="px-3 py-2 font-semibold text-slate-800">
                                    Source Scheme
                                    {rec.source_scheme_name && <span className="block text-xs font-normal text-slate-500">{rec.source_scheme_name}</span>}
                                    {rec.source_fund_inception_date && (
                                      <span className="block text-xs font-normal italic text-slate-500">
                                        Inception: {new Date(rec.source_fund_inception_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-right text-slate-900">{pct(rec.source_returns_1y)}</td>
                                  <td className="px-3 py-2 text-right text-slate-900">{pct(rec.source_returns_3y)}</td>
                                  <td className="px-3 py-2 text-right text-slate-900">{pct(rec.source_returns_5y)}</td>
                                  <td className="px-3 py-2 text-right text-slate-900">{pct(rec.source_returns_since_inception)}</td>
                                </tr>
                                <tr className="border-t border-slate-200 bg-amber-50/60">
                                  <td className="px-3 py-2 font-semibold text-slate-800">
                                    Source Benchmark
                                    {rec.source_benchmark_name && <span className="block text-xs font-normal text-slate-500">{rec.source_benchmark_name}</span>}
                                  </td>
                                  <td className="px-3 py-2 text-right text-slate-900">{pct(rec.source_bench_1y)}</td>
                                  <td className="px-3 py-2 text-right text-slate-900">{pct(rec.source_bench_3y)}</td>
                                  <td className="px-3 py-2 text-right text-slate-900">{pct(rec.source_bench_5y)}</td>
                                  <td className="px-3 py-2 text-right text-slate-900">{pct(rec.source_bench_since_inception)}</td>
                                </tr>
                              </>
                            )}
                            <tr className="border-t border-slate-200">
                              <td className="px-3 py-2 font-semibold text-slate-800">
                                {targetSchemeLabel}
                                {(rec.target_scheme_name || rec.scheme_name) && <span className="block text-xs font-normal text-slate-500">{rec.target_scheme_name || rec.scheme_name}</span>}
                                {rec.fund_inception_date && (
                                  <span className="block text-xs font-normal italic text-slate-500">
                                    Inception: {new Date(rec.fund_inception_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right text-slate-900">{pct(rec.returns_1y)}</td>
                              <td className="px-3 py-2 text-right text-slate-900">{pct(rec.returns_3y)}</td>
                              <td className="px-3 py-2 text-right text-slate-900">{pct(rec.returns_5y)}</td>
                              <td className="px-3 py-2 text-right text-slate-900">{pct(rec.returns_since_inception)}</td>
                            </tr>
                            <tr className="border-t border-slate-200 bg-slate-50">
                              <td className="px-3 py-2 font-semibold text-slate-800">
                                {isDual ? 'Target Benchmark' : 'Benchmark'}
                                {rec.benchmark_name && <span className="block text-xs font-normal text-slate-500">{rec.benchmark_name}</span>}
                              </td>
                              <td className="px-3 py-2 text-right text-slate-900">{pct(rec.bench_1y)}</td>
                              <td className="px-3 py-2 text-right text-slate-900">{pct(rec.bench_3y)}</td>
                              <td className="px-3 py-2 text-right text-slate-900">{pct(rec.bench_5y)}</td>
                              <td className="px-3 py-2 text-right text-slate-900">{pct(rec.bench_since_inception)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                        );
                    })()}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Additional Notes */}
          {proposal.notes && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Additional Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700">{proposal.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Dates */}
          <div className="grid md:grid-cols-3 gap-4 mb-6 text-sm">
            <div className="bg-slate-50 rounded p-3">
              <p className="text-slate-600 flex items-center gap-2">
                <Calendar size={16} />
                Created
              </p>
              <p className="font-semibold text-slate-900">{formatDate(proposal.created_at)}</p>
            </div>
            <div className="bg-slate-50 rounded p-3">
              <p className="text-slate-600 flex items-center gap-2">
                <Calendar size={16} />
                Last Updated
              </p>
              <p className="font-semibold text-slate-900">{formatDate(proposal.updated_at)}</p>
            </div>
            <div className="bg-slate-50 rounded p-3">
              <p className="text-slate-600 flex items-center gap-2">
                <Calendar size={16} />
                Valid Until
              </p>
              <p className="font-semibold text-slate-900">{formatDate(proposal.valid_until)}</p>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-bold text-red-900 mb-2">Important Disclaimer</p>
                <p className="text-red-800 text-xs leading-relaxed">
                  Mutual Fund investments are subject to market risks, read all scheme related documents carefully. 
                  The NAVs of the schemes may go up or down depending upon the factors and forces affecting the 
                  securities market including the fluctuations in the interest rates. The past performance of the 
                  mutual funds is not necessarily indicative of future performance of the schemes. The Mutual Fund 
                  is not guaranteeing or assuring any dividend under any of the schemes and the same is subject to 
                  the availability and adequacy of distributable surplus. Investors are requested to review the 
                  prospectus carefully and obtain expert professional advice with regard to specific legal, tax and 
                  financial implications of the investment. <strong>Rubicon Finservices is AMFI Registered Mutual 
                  Fund Distributor (ARN: 102867).</strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button 
            onClick={downloadPDF}
            className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
          >
            <Download size={20} />
            Download PDF
          </Button>
          <Link to={`/admin/proposals/${id}/edit`}>
            <Button className="bg-blue-600 hover:bg-blue-700">
              Edit Proposal
            </Button>
          </Link>
          <Link to="/admin/dashboard">
            <Button variant="outline">
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ViewProposal;
